import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, normalizeStyle as _normalizeStyle, withCtx as _withCtx, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock, Fragment as _Fragment } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "settings-container"
}
const _hoisted_2 = { class: "summary-labels" }
const _hoisted_3 = { class: "horizontal-container" }
const _hoisted_4 = { class: "summary-labels" }
const _hoisted_5 = { class: "horizontal-container" }
const _hoisted_6 = { class: "summary-labels" }
const _hoisted_7 = { class: "summary-label" }
const _hoisted_8 = { class: "summary-value" }
const _hoisted_9 = ["onClick"]
const _hoisted_10 = { class: "buttons-block" }
const _hoisted_11 = { class: "summary-labels upload-block" }

import { computed, onMounted, ref } from "vue";
import { useToast } from "vue-toastification";
import { useI18n } from "vue-i18n";
import { useMutation, useQuery } from "@vue/apollo-composable";
import { AoColorPicker } from "@26lights/orcha";

import enUK from "@/locales/project/en-UK.json";

import ButtonIcon from "@/components/ButtonIcon.vue";
import ImageUploader from "@/components/ImageUploader.vue";
import EditProject from "@/components/CRUXEntity/EditProject.vue";
import TransferProject from "@/components/CRUXEntity/TransferProject.vue";
import AddResource from "@/components/CRUXEntity/AddResource.vue";
import DeleteResource from "@/components/CRUXEntity/DeleteResource.vue";
import ProjectTeam from "@/components/project/ProjectTeam.vue";
import ProjectStakeholders from "@/components/project/ProjectStakeholders.vue";

import checkHasAccess from "@/helpers/permissions/checkHasAccess";
import {
  ATTACH_ARCHITECTURE_PICTURE,
  GET_BASIC_PROJECT,
  GET_PROJECT_RESOURCES,
} from "@/graphql/project/queries";
import { UPDATE_PROJECT } from "@/graphql/project/mutations";
import useMutationHandlers from "@/composables/useMutationHandlers";
import { ProjectResource } from "@/helpers/typesDefinition";

type MessageSchema = typeof enUK;

interface Props {
  id: string;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'ProjectSettings',
  props: {
    id: {}
  },
  setup(__props: any) {

const props = __props;

const { t } = useI18n<[MessageSchema], "en-UK">({
  inheritLocale: true,
  messages: {
    "en-UK": enUK,
  },
});

const toast = useToast();
const hasAdminAccess = ref(false);
const { createErrorHandler, createResultHandler } = useMutationHandlers();

onMounted(async () => {
  hasAdminAccess.value = await checkHasAccess(
    undefined,
    props.id,
    "admin",
    toast,
    t("errors.getUserDetails"),
  );
});

const { result: projectResult, onError: onProjectError } = useQuery(
  GET_BASIC_PROJECT,
  () => ({
    id: props.id,
  }),
);

onProjectError(createErrorHandler("getProject"));

const project = computed(() => projectResult.value?.project);

const {
  mutate: attachArchitecturePicture,
  onError: onAttachArchitecturePictureError,
  onDone: onAttachArchitecturePictureDone,
} = useMutation(ATTACH_ARCHITECTURE_PICTURE, {
  refetchQueries: [
    {
      query: GET_BASIC_PROJECT,
      variables: {
        id: props.id,
      },
    },
  ],
});

onAttachArchitecturePictureError(
  createErrorHandler("attachArchitectureImageToProject"),
);

onAttachArchitecturePictureDone(
  createResultHandler("attachArchitectureImageToProject", () => {
    toast.success(t("success.architecturePictureUpdated"));
  }),
);

const {
  mutate: updateProjectColor,
  onError: onUpdateProjectColorError,
  onDone: onUpdateProjectColorDone,
} = useMutation(UPDATE_PROJECT, {
  refetchQueries: [
    {
      query: GET_BASIC_PROJECT,
      variables: {
        id: props.id,
      },
    },
  ],
});

onUpdateProjectColorError(createErrorHandler("updateProject"));

onUpdateProjectColorDone(
  createResultHandler("updateProject", () => {
    toast.success(t("success.edit"));
  }),
);

const projectColor = computed({
  get: () => project.value?.color || "#000000",
  set: async (newColor: string) => {
    await updateProjectColor({
      id: props.id,
      color: newColor,
      defaultPaymentOption: project.value.defaultPaymentOption,
      budgetTracking: project.value.budgetTracking,
      plannedTimeTracking: project.value.plannedTimeTracking,
      estimationUnit: project.value.estimationUnit,
    });
  },
});

const { result: projectResourcesResult, onError: onProjectResourcesError } =
  useQuery(GET_PROJECT_RESOURCES, () => ({
    projectId: props.id,
  }));

onProjectResourcesError(createErrorHandler("getProjectResources"));

const projectResources = computed<ProjectResource[]>(() => {
  return projectResourcesResult.value?.projectResources ?? [];
});

const isEditProjectModalVisible = ref(false);

function showEditProjectModal(): void {
  isEditProjectModalVisible.value = true;
}

function closeEditProjectModal(): void {
  isEditProjectModalVisible.value = false;
}

const isTransferProjectModalVisible = ref(false);

function showTransferProjectModal(): void {
  isTransferProjectModalVisible.value = true;
}

function closeTransferProjectModal(): void {
  isTransferProjectModalVisible.value = false;
}

async function onNewArchitecturePicture(fileId: string): Promise<void> {
  await attachArchitecturePicture({ projectId: props.id, fileId });
}

async function onClearArchitecturePicture(): Promise<void> {
  await attachArchitecturePicture({
    projectId: props.id,
    fileId: undefined,
  });
}

const isAddResourceModalVisible = ref(false);

function showAddResourceModal(): void {
  isAddResourceModalVisible.value = true;
}

function closeAddResourcesModal(): void {
  isAddResourceModalVisible.value = false;
}

const isDeleteResourceModalVisible = ref(false);

function showDeleteResourceModal(): void {
  isDeleteResourceModalVisible.value = true;
}

function closeDeleteResourcesModal(): void {
  isDeleteResourceModalVisible.value = false;
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    (project.value && hasAdminAccess.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
          _createElementVNode("div", _hoisted_2, _toDisplayString(_unref(t)("labels.defaultPaymentOption")), 1),
          _createElementVNode("div", _hoisted_3, [
            _createElementVNode("p", null, _toDisplayString(_unref(t)(`paymentOptionLabels.${project.value.defaultPaymentOption}`)), 1)
          ]),
          _createElementVNode("div", _hoisted_4, _toDisplayString(_unref(t)("labels.needsBudgetApproval")), 1),
          _createElementVNode("div", _hoisted_5, [
            _createElementVNode("p", null, _toDisplayString(project.value.needsBudgetApproval), 1)
          ]),
          _createElementVNode("div", _hoisted_6, [
            _createElementVNode("span", _hoisted_7, _toDisplayString(_unref(t)("labels.color")), 1),
            _createElementVNode("span", _hoisted_8, [
              _createVNode(_unref(AoColorPicker), {
                color: projectColor.value,
                "onUpdate:color": _cache[0] || (_cache[0] = ($event: any) => ((projectColor).value = $event))
              }, {
                clickable: _withCtx(({ openPicker, bgColor }) => [
                  _createElementVNode("button", {
                    type: "button",
                    class: "color-swatch",
                    style: _normalizeStyle({ backgroundColor: bgColor }),
                    onClick: (e) => openPicker(e.currentTarget as HTMLElement)
                  }, null, 12, _hoisted_9)
                ]),
                _: 1
              }, 8, ["color"])
            ])
          ]),
          _createElementVNode("div", _hoisted_10, [
            _createVNode(ButtonIcon, {
              label: _unref(t)('buttons.edit'),
              "icon-name": "jam:pen-f",
              onClick: _cache[1] || (_cache[1] = ($event: any) => (showEditProjectModal()))
            }, null, 8, ["label"]),
            _createVNode(ButtonIcon, {
              label: _unref(t)('buttons.copy'),
              "icon-name": "heroicons-outline:document-duplicate",
              onClick: _cache[2] || (_cache[2] = ($event: any) => (showTransferProjectModal()))
            }, null, 8, ["label"]),
            _createVNode(ButtonIcon, {
              label: _unref(t)('buttons.addResources'),
              "icon-name": "material-symbols:add",
              onClick: _cache[3] || (_cache[3] = ($event: any) => (showAddResourceModal()))
            }, null, 8, ["label"]),
            (projectResources.value.length > 0)
              ? (_openBlock(), _createBlock(ButtonIcon, {
                  key: 0,
                  label: _unref(t)('buttons.deleteResources'),
                  "icon-name": "material-symbols:delete",
                  onClick: _cache[4] || (_cache[4] = ($event: any) => (showDeleteResourceModal()))
                }, null, 8, ["label"]))
              : _createCommentVNode("", true)
          ]),
          _createElementVNode("div", _hoisted_11, _toDisplayString(_unref(t)("labels.architecturePicture")), 1),
          _createVNode(ImageUploader, {
            "current-url": project.value.architectureImage?.url,
            onChanged: onNewArchitecturePicture,
            onCleared: onClearArchitecturePicture
          }, null, 8, ["current-url"]),
          _createVNode(ProjectStakeholders, {
            "project-id": props.id
          }, null, 8, ["project-id"]),
          _createVNode(ProjectTeam, {
            "project-id": props.id
          }, null, 8, ["project-id"])
        ]))
      : _createCommentVNode("", true),
    (
      project.value && project.value.active && hasAdminAccess.value && isEditProjectModalVisible.value
    )
      ? (_openBlock(), _createBlock(EditProject, {
          key: 1,
          onClose: _cache[5] || (_cache[5] = ($event: any) => (closeEditProjectModal())),
          project: project.value
        }, null, 8, ["project"]))
      : _createCommentVNode("", true),
    (project.value && hasAdminAccess.value && isTransferProjectModalVisible.value)
      ? (_openBlock(), _createBlock(TransferProject, {
          key: 2,
          onClose: _cache[6] || (_cache[6] = ($event: any) => (closeTransferProjectModal())),
          project: project.value
        }, null, 8, ["project"]))
      : _createCommentVNode("", true),
    (isAddResourceModalVisible.value)
      ? (_openBlock(), _createBlock(AddResource, {
          key: 3,
          onClose: _cache[7] || (_cache[7] = ($event: any) => (closeAddResourcesModal())),
          "project-id": props.id
        }, null, 8, ["project-id"]))
      : _createCommentVNode("", true),
    (isDeleteResourceModalVisible.value)
      ? (_openBlock(), _createBlock(DeleteResource, {
          key: 4,
          onClose: _cache[8] || (_cache[8] = ($event: any) => (closeDeleteResourcesModal())),
          "project-id": props.id,
          "project-resources": projectResources.value
        }, null, 8, ["project-id", "project-resources"]))
      : _createCommentVNode("", true)
  ], 64))
}
}

})